import {
  Building2,
  Cloud,
  Database,
  Globe,
  Key,
  Plus,
  RefreshCw,
  Search,
  ShoppingBag,
  SlidersHorizontal,
  Sparkles,
  Store,
  Trash2,
  Undo2,
  X
} from 'lucide-react';
import React, { useEffect, useState } from 'react';
import { MOCK_BOOKS } from '../../../../mocks/Books.mocks';
import { StoresPanel } from './StoresPanel';
import './SalesChannelView.css';

import { CHANNEL_METRICS, INITIAL_CHANNEL_ENTITIES, PRESET_CLIENTS } from '../../../../mocks/SalesChannel.mocks';
import { AppLog, ChannelEntity } from '../../../../types/SalesChannel.types';
import { ModuleHeader } from '../../../../components/shared/ModuleHeader/ModuleHeader';
import { StatCard } from '../../../../components/shared/StatCard/StatCard';
import { useToast } from '../../../../hooks/useToast';
import Modal from '../../../../components/shared/Modal/Modal';
import { DataTable, ColumnDef } from '../../../../components/shared/DataTable/DataTable';
import { MasterDetailLayout } from '../../../../components/shared/MasterDetail/MasterDetailLayout';
import { RowActionsMenu, viewAction } from '../../../../components/shared/RowActionsMenu/RowActionsMenu';
import { DetailPanel } from '../../../../components/shared/DetailPanel/DetailPanel';
import { DetailField, DetailFieldList, DetailIdentity } from '../../../../components/shared/DetailPanel/DetailBlocks';
import { useClickOutside, isDetailKeepZone } from '../../../../hooks/useClickOutside';
import '../shared-modules.css';

export const SalesChannelView = ({ privacyMode = false }: { privacyMode?: boolean }) => {
  const { notify } = useToast();
  const [activeChannel, setActiveChannel] = useState<'librerias' | 'online' | 'baas' | 'partners'>('librerias');
  const [searchQuery, setSearchQuery] = useState('');
  const [selectedEntityId, setSelectedEntityId] = useState<string>('');
  const [syncingId, setSyncingId] = useState<string | null>(null);
  const [marginModalOpen, setMarginModalOpen] = useState(false);
  const [marginModalEntityId, setMarginModalEntityId] = useState('');
  const [clearLogsModalOpen, setClearLogsModalOpen] = useState(false);
  const [customMargin, setCustomMargin] = useState('45%');

  const [entitiesState, setEntitiesState] = useState<Record<string, ChannelEntity[]>>(() => {
    const saved = localStorage.getItem('albatross_canal_entities');
    return saved ? JSON.parse(saved) : INITIAL_CHANNEL_ENTITIES;
  });

  const [journalEntries, setJournalEntries] = useState<any[]>(() => {
    const saved = localStorage.getItem('albatross_asientos_contables');
    return saved ? JSON.parse(saved) : [];
  });

  const [logsState, setLogsState] = useState<AppLog[]>(() => {
    const saved = localStorage.getItem('albatross_canal_logs');
    if (saved) return JSON.parse(saved);
    return [
      { time: '2026-07-27 20:18:42', id: 'LIBRERIAS-092', event: 'Sincronización incremental de catálogo', status: 'SUCCESS', log: '24 libros actualizados' },
      { time: '2026-07-27 19:42:15', id: 'ONLINE-091', event: 'Llamada API entrante (Webhook pedido)', status: 'SUCCESS', log: '+450.00€ facturado' },
      { time: '2026-07-27 18:05:01', id: 'ONLINE-090', event: 'Validación de stock mínimo disparada', status: 'SUCCESS', log: 'Stock consolidado OK' },
      { time: '2026-07-27 15:30:11', id: 'BAAS-089', event: 'Ping periódico de salud del servidor', status: 'SUCCESS', log: 'Latencia OK (24ms)' },
      { time: '2026-07-27 12:12:00', id: 'PARTNERS-088', event: 'Actualización masiva de precios', status: 'SUCCESS', log: '1,500 registros actualizados' },
    ];
  });

  useEffect(() => {
    localStorage.setItem('albatross_canal_entities', JSON.stringify(entitiesState));
  }, [entitiesState]);

  useEffect(() => {
    localStorage.setItem('albatross_asientos_contables', JSON.stringify(journalEntries));
  }, [journalEntries]);

  useEffect(() => {
    localStorage.setItem('albatross_canal_logs', JSON.stringify(logsState));
  }, [logsState]);

  const [activeActionModalId, setActiveActionModalId] = useState<string | null>(null);

  // Forms
  const [orderEntityId, setOrderEntityId] = useState<string>('');
  const [clientSearchQuery, setClientSearchQuery] = useState('');
  const [showClientDropdown, setShowClientDropdown] = useState(false);
  const [selectedClient, setSelectedClient] = useState<any | null>(null);
  const [invoiceLines, setInvoiceLines] = useState<Array<{
    id: string;
    product: any | null;
    customName: string;
    quantity: number;
    price: number;
    vat: number;
  }>>([
    { id: '1', product: MOCK_BOOKS[0], customName: MOCK_BOOKS[0].title, quantity: 10, price: MOCK_BOOKS[0].price, vat: 4 }
  ]);

  const [returnEntityId, setReturnEntityId] = useState<string>('');
  const [returnClientSearchQuery, setReturnClientSearchQuery] = useState('');
  const [showReturnClientDropdown, setShowReturnClientDropdown] = useState(false);
  const [selectedReturnClient, setSelectedReturnClient] = useState<any | null>(null);
  const [returnProduct, setReturnProduct] = useState<any | null>(MOCK_BOOKS[0]);
  const [returnQty, setReturnQty] = useState<number>(5);
  const [returnPrice, setReturnPrice] = useState<number>(MOCK_BOOKS[0].price);
  const [returnVat, setReturnVat] = useState<number>(4);

  const [newEntityName, setNewEntityName] = useState('');
  const [newEntityId, setNewEntityId] = useState('');
  const [newEntityStatus, setNewEntityStatus] = useState<'online' | 'offline' | 'error'>('online');
  const [newEntityLatency, setNewEntityLatency] = useState('24ms');
  const [newEntityExtraLabel, setNewEntityExtraLabel] = useState('Límite Crédito');
  const [newEntityExtraValue, setNewEntityExtraValue] = useState('30.000€');

  const [marginEntityId, setMarginEntityId] = useState('');
  const [marginPercent, setMarginPercent] = useState('45%');

  const currentEntities = entitiesState[activeChannel] || [];
  const metrics = CHANNEL_METRICS[activeChannel];

  useEffect(() => {
    if (currentEntities.length > 0) {
      setOrderEntityId(currentEntities[0].id);
      setReturnEntityId(currentEntities[0].id);
      setMarginEntityId(currentEntities[0].id);
    }
  }, [activeChannel, entitiesState]);

  const selectedEntity = currentEntities.find(e => e.id === selectedEntityId) || null;
  // Entidad del modal de márgenes: puede abrirse desde el menú de fila sin abrir la ficha.
  const marginEntity = currentEntities.find(e => e.id === marginModalEntityId) || null;

  // La ficha de entidad es de solo lectura: un clic fuera la cierra. Los
  // modales de esta vista van dentro de #root y llevan data-keep-detail.
  useClickOutside(isDetailKeepZone, () => setSelectedEntityId(''), Boolean(selectedEntity));

  const handleSyncEntity = (id: string, name: string) => {
    setSyncingId(id);
    notify('Sincronización', `Iniciando sincronización de stock y catálogo con ${name}...`, 'info');
    
    const timestamp = new Date().toISOString().replace('T', ' ').substring(0, 19);
    const newLog: AppLog = {
      time: timestamp,
      id: `${activeChannel.toUpperCase()}-${Math.floor(100 + Math.random() * 900)}`,
      event: `Sincronización forzada con ${name}`,
      status: 'SUCCESS',
      log: 'Conexión REST API Ok. 0 discrepancias de stock.'
    };
    
    setTimeout(() => {
      setSyncingId(null);
      setLogsState(prev => [newLog, ...prev]);
      notify('Sincronización exitosa', `Sincronización exitosa con ${name}. Catálogo actualizado.`, 'info');
    }, 2000);
  };

  const handleApplyMargin = () => {
    setMarginModalOpen(false);
    if (marginEntity) {
      setEntitiesState(prev => {
        const next = { ...prev };
        next[activeChannel] = next[activeChannel].map(e => {
          if (e.id === marginEntity.id) {
            return { ...e, extraValue: customMargin };
          }
          return e;
        });
        return next;
      });
    }

    const timestamp = new Date().toISOString().replace('T', ' ').substring(0, 19);
    const newLog: AppLog = {
      time: timestamp,
      id: `${activeChannel.toUpperCase()}-MARG`,
      event: `Modificación de margen comercial`,
      status: 'SUCCESS',
      log: `${marginEntity?.name || 'Canal'} ajustado a ${customMargin}`
    };
    setLogsState(prev => [newLog, ...prev]);
    notify('Margen comercial', `Se ha actualizado el margen de distribución para ${marginEntity?.name} a ${customMargin}.`, 'info');
  };

  const addInvoiceLine = () => {
    const nextId = String(invoiceLines.length + 1);
    setInvoiceLines(prev => [
      ...prev,
      { id: nextId, product: MOCK_BOOKS[0], customName: MOCK_BOOKS[0].title, quantity: 5, price: MOCK_BOOKS[0].price, vat: 4 }
    ]);
  };

  const removeInvoiceLine = (id: string) => {
    if (invoiceLines.length > 1) {
      setInvoiceLines(prev => prev.filter(l => l.id !== id));
    }
  };

  const handleProductChange = (lineId: string, prodId: string) => {
    const prod = MOCK_BOOKS.find(p => p.id === prodId);
    setInvoiceLines(prev => prev.map(line => {
      if (line.id === lineId) {
        return {
          ...line,
          product: prod || null,
          customName: prod ? prod.title : '',
          price: prod ? prod.price : 0,
          vat: 4
        };
      }
      return line;
    }));
  };

  const handleLineFieldChange = (lineId: string, field: string, value: any) => {
    setInvoiceLines(prev => prev.map(line => {
      if (line.id === lineId) {
        return { ...line, [field]: value };
      }
      return line;
    }));
  };

  const handleSubmitChannelOrder = (e: React.FormEvent) => {
    e.preventDefault();
    if (!selectedClient) {
      notify('Aviso', 'Por favor identifique un cliente para procesar el pedido.', 'warning');
      return;
    }

    const invalidLine = invoiceLines.some(l => !l.product && !l.customName);
    if (invalidLine) {
      notify('Aviso', 'Asegúrese de seleccionar un producto o ingresar un concepto para cada línea.', 'warning');
      return;
    }

    let subtotal = 0;
    const vatBreakdown: Record<number, number> = {};
    invoiceLines.forEach(line => {
      const lineBase = line.quantity * line.price;
      subtotal += lineBase;
      const lineVat = lineBase * (line.vat / 100);
      vatBreakdown[line.vat] = (vatBreakdown[line.vat] || 0) + lineVat;
    });

    const totalVat = Object.values(vatBreakdown).reduce((a, b) => a + b, 0);
    const totalOrder = subtotal + totalVat;

    const linesToPost = [
      { account: '430000', accountName: `Clientes (Euros) - ${selectedClient.name}`, debit: parseFloat(totalOrder.toFixed(2)), credit: 0 },
      { account: '700000', accountName: 'Ventas de mercaderías (Canal)', debit: 0, credit: parseFloat(subtotal.toFixed(2)) }
    ];

    Object.keys(vatBreakdown).forEach(rateKey => {
      const rate = parseInt(rateKey);
      const amount = parseFloat(vatBreakdown[rate].toFixed(2));
      if (amount > 0) {
        linesToPost.push({ account: '477000', accountName: `H.P. IVA Repercutido (${rate}%)`, debit: 0, credit: amount });
      }
    });

    const nextIdNum = journalEntries.length + 1;
    const nextIdStr = `AS-2026-${String(nextIdNum).padStart(3, '0')}`;
    const newEntry = {
      id: nextIdStr,
      date: new Date().toISOString().split('T')[0],
      concept: `PEDIDO ENTRANTE CANAL ${activeChannel.toUpperCase()} - DOC: ${nextIdStr}`,
      status: 'posted',
      sourceModule: 'Canal de Venta',
      lines: linesToPost
    };

    setJournalEntries(prev => [...prev, newEntry]);

    const entityObj = currentEntities.find(ent => ent.id === orderEntityId);
    if (entityObj) {
      setEntitiesState(prev => {
        const next = { ...prev };
        next[activeChannel] = next[activeChannel].map(ent => {
          if (ent.id === orderEntityId) {
            const currentSalesVal = parseFloat(ent.sales.replace('€', '').replace('.', '')) || 0;
            const updatedSalesStr = `${(currentSalesVal + totalOrder).toLocaleString('es-ES')}€`;
            return { ...ent, orders: ent.orders + 1, sales: updatedSalesStr, lastSync: 'Hace un momento' };
          }
          return ent;
        });
        return next;
      });
    }

    const timestamp = new Date().toISOString().replace('T', ' ').substring(0, 19);
    const newLog: AppLog = {
      time: timestamp,
      id: `${activeChannel.toUpperCase()}-PED`,
      event: `Facturación canal: Pedido procesado`,
      status: 'SUCCESS',
      log: `Reg. Asiento ${nextIdStr} por ${totalOrder.toFixed(2)}€ para ${selectedClient.name}`
    };
    setLogsState(prev => [newLog, ...prev]);

    setActiveActionModalId(null);
    notify('Pedido procesado', `Pedido de canal registrado con éxito. Generado Asiento Contable ${nextIdStr} de forma automática.`, 'info');
    
    setClientSearchQuery('');
    setSelectedClient(null);
    setInvoiceLines([{ id: '1', product: MOCK_BOOKS[0], customName: MOCK_BOOKS[0].title, quantity: 10, price: MOCK_BOOKS[0].price, vat: 4 }]);
  };

  const handleSubmitReturn = (e: React.FormEvent) => {
    e.preventDefault();
    if (!selectedReturnClient) {
      notify('Aviso', 'Por favor identifique un cliente para procesar la devolución.', 'warning');
      return;
    }

    const returnBase = returnQty * returnPrice;
    const returnVatAmount = returnBase * (returnVat / 100);
    const returnTotal = returnBase + returnVatAmount;

    const linesToPost = [
      { account: '700000', accountName: 'Ventas de mercaderías (Devolución Canal)', debit: parseFloat(returnBase.toFixed(2)), credit: 0 },
      { account: '477000', accountName: `H.P. IVA Repercutido (${returnVat}% Reversa)`, debit: parseFloat(returnVatAmount.toFixed(2)), credit: 0 },
      { account: '430000', accountName: `Clientes (Euros) - ${selectedReturnClient.name}`, debit: 0, credit: parseFloat(returnTotal.toFixed(2)) }
    ];

    const nextIdNum = journalEntries.length + 1;
    const nextIdStr = `AS-2026-${String(nextIdNum).padStart(3, '0')}`;
    const newEntry = {
      id: nextIdStr,
      date: new Date().toISOString().split('T')[0],
      concept: `DEVOLUCIÓN ACEPTADA CANAL ${activeChannel.toUpperCase()} - DOC: ${nextIdStr}`,
      status: 'posted',
      sourceModule: 'Canal de Venta',
      lines: linesToPost
    };

    setJournalEntries(prev => [...prev, newEntry]);

    const entityObj = currentEntities.find(ent => ent.id === returnEntityId);
    if (entityObj) {
      setEntitiesState(prev => {
        const next = { ...prev };
        next[activeChannel] = next[activeChannel].map(ent => {
          if (ent.id === returnEntityId) {
            const currentSalesVal = parseFloat(ent.sales.replace('€', '').replace('.', '')) || 0;
            const updatedSalesStr = `${Math.max(0, currentSalesVal - returnTotal).toLocaleString('es-ES')}€`;
            return { ...ent, sales: updatedSalesStr, lastSync: 'Hace un momento' };
          }
          return ent;
        });
        return next;
      });
    }

    const timestamp = new Date().toISOString().replace('T', ' ').substring(0, 19);
    const newLog: AppLog = {
      time: timestamp,
      id: `${activeChannel.toUpperCase()}-RET`,
      event: `Devolución canal: Abono procesado`,
      status: 'SUCCESS',
      log: `Reg. Asiento ${nextIdStr} (Abono) por ${returnTotal.toFixed(2)}€ para ${selectedReturnClient.name}`
    };
    setLogsState(prev => [newLog, ...prev]);

    setActiveActionModalId(null);
    notify('Devolución procesada', `Devolución procesada. Generada Nota de Crédito / Asiento ${nextIdStr}.`, 'info');
    
    setReturnClientSearchQuery('');
    setSelectedReturnClient(null);
  };

  const handleSubmitNewIntegration = (e: React.FormEvent) => {
    e.preventDefault();
    if (!newEntityName || !newEntityId) {
      notify('Aviso', 'Por favor ingrese el nombre y un identificador único.', 'warning');
      return;
    }

    const newEntityObj: ChannelEntity = {
      id: newEntityId.toUpperCase(),
      name: newEntityName,
      status: newEntityStatus,
      lastSync: 'Sincronizado hoy',
      sales: '0€',
      orders: 0,
      apiLatency: newEntityLatency,
      extraLabel: newEntityExtraLabel,
      extraValue: newEntityExtraValue
    };

    setEntitiesState(prev => {
      const next = { ...prev };
      next[activeChannel] = [...next[activeChannel], newEntityObj];
      return next;
    });

    const timestamp = new Date().toISOString().replace('T', ' ').substring(0, 19);
    const newLog: AppLog = {
      time: timestamp,
      id: `${activeChannel.toUpperCase()}-NEW`,
      event: `Nueva Integración API Registrada`,
      status: 'SUCCESS',
      log: `Entidad ${newEntityName} conectada bajo protocolo REST.`
    };
    setLogsState(prev => [newLog, ...prev]);

    setActiveActionModalId(null);
    notify('Integración agregada', `Integración con ${newEntityName} agregada al canal ${activeChannel} con éxito.`, 'info');
    
    setNewEntityName('');
    setNewEntityId('');
  };

  const handleSubmitMarginChange = (e: React.FormEvent) => {
    e.preventDefault();
    setEntitiesState(prev => {
      const next = { ...prev };
      next[activeChannel] = next[activeChannel].map(e => {
        if (e.id === marginEntityId) {
          return { ...e, extraValue: marginPercent };
        }
        return e;
      });
      return next;
    });

    const entityName = currentEntities.find(ent => ent.id === marginEntityId)?.name || 'Entidad';
    const timestamp = new Date().toISOString().replace('T', ' ').substring(0, 19);
    const newLog: AppLog = {
      time: timestamp,
      id: `${activeChannel.toUpperCase()}-MARG`,
      event: `Margen Acuerdo Modificado`,
      status: 'SUCCESS',
      log: `Ajustado margen para ${entityName} a ${marginPercent}`
    };
    setLogsState(prev => [newLog, ...prev]);

    setActiveActionModalId(null);
    notify('Margen ajustado', `Margen de distribución para ${entityName} ajustado a ${marginPercent}.`, 'info');
  };

  const filteredEntities = currentEntities.filter(entity => 
    entity.name.toLowerCase().includes(searchQuery.toLowerCase()) ||
    entity.id.toLowerCase().includes(searchQuery.toLowerCase())
  );

  const STATUS_LABELS: Record<ChannelEntity['status'], string> = { online: 'CONECTADO', offline: 'DESCONECTADO', error: 'ERROR' };
  const STATUS_BADGE: Record<ChannelEntity['status'], string> = { online: 'active', offline: 'pending', error: 'blocked' };

  const entityColumns: ColumnDef<ChannelEntity>[] = [
    { id: 'id', header: 'ID', render: (e) => <span className="sales-channel-entity-id">{e.id}</span> },
    {
      id: 'name',
      header: 'ENTIDAD / CANAL',
      render: (e) => (
        <div className="sales-channel-entity-btn-left">
          <div className={`sales-channel-status-dot ${e.status}`} />
          <span className="sales-channel-entity-name">{e.name}</span>
        </div>
      )
    },
    { id: 'status', header: 'ESTADO', render: (e) => <span className={`module-badge ${STATUS_BADGE[e.status]}`}>{STATUS_LABELS[e.status]}</span> },
    { id: 'sales', header: 'VENTAS', align: 'right', render: (e) => (privacyMode ? '•••••' : e.sales) },
    { id: 'orders', header: 'PEDIDOS', align: 'right', render: (e) => (privacyMode ? '•••••' : e.orders) },
    { id: 'apiLatency', header: 'LATENCIA' },
    { id: 'lastSync', header: 'ÚLTIMA SINCRO' },
    { id: 'extra', header: 'PARÁMETRO CLAVE', render: (e) => <span>{e.extraLabel}: <strong>{e.extraValue}</strong></span> }
  ];

  const openMarginModal = (entity: ChannelEntity) => {
    setMarginModalEntityId(entity.id);
    setCustomMargin(entity.extraValue);
    setMarginModalOpen(true);
  };

  return (
    <div className="sales-channel-container">
      <ModuleHeader
        title="Canales_De_Venta"
        subtitle="Gestión, sincronización y monitorización de librerías, tiendas online y partners."
      />

      {/* Tabs */}
      <div className="sales-channel-nav">
        {[
          { id: 'librerias', label: 'Librerías', icon: Store },
          { id: 'online', label: 'Canales Online', icon: Globe },
          { id: 'baas', label: 'Libro como Servicio', icon: Cloud },
          { id: 'partners', label: 'Partners & Distribución', icon: Building2 }
        ].map((channel) => {
          const IconComp = channel.icon;
          return (
            <button
              key={channel.id}
              onClick={() => {
                setActiveChannel(channel.id as any);
                setSelectedEntityId('');
                notify('Canal de ventas', `Cargando métricas y entidades para: Canal ${channel.label}`, 'info');
              }}
              className={`sales-channel-nav-btn ${activeChannel === channel.id ? 'active' : ''}`}
            >
              <IconComp size={12} />
              {channel.label}
            </button>
          );
        })}
      </div>

      {/* Top Action Bar */}
      <div className="sales-channel-actions-bar">
        <div className="sales-channel-actions-header">
          <span className="sales-channel-actions-title">Opciones corporativas de Canal [Botonera superior]</span>
          <div className="sales-channel-actions-list">
            {[
              { id: 'canal_pedido', label: 'Nuevo Pedido Canal', icon: ShoppingBag },
              { id: 'canal_devolucion', label: 'Registrar Devolución', icon: Undo2 },
              { id: 'canal_nueva_api', label: 'Nueva Integración API', icon: Plus },
              { id: 'canal_margenes', label: 'Ajustar Márgenes', icon: SlidersHorizontal },
              { id: 'canal_logs', label: 'Auditoría Logs API', icon: Key }
            ].map(act => {
              const Icon = act.icon;
              return (
                <button
                  key={act.id}
                  onClick={() => {
                    setActiveActionModalId(act.id);
                    if (act.id === 'canal_margenes') {
                      setMarginPercent(selectedEntity?.extraValue || '45%');
                    }
                  }}
                  className="sales-channel-action-btn"
                >
                  <Icon size={12} className="sales-channel-action-icon" />
                  <span>{act.label}</span>
                </button>
              );
            })}
          </div>
        </div>
      </div>

      {/* Metrics Grid */}
      <div className="sales-channel-metrics-grid">
        <StatCard 
          label="Ventas_Canal" 
          value={privacyMode ? '•••••' : metrics.sales} 
          trend={!privacyMode ? metrics.trend1 : undefined} 
        />
        <StatCard 
          label="Pedidos_Recibidos" 
          value={privacyMode ? '•••••' : metrics.orders} 
          trend={!privacyMode ? metrics.trend2 : undefined} 
        />
        <StatCard 
          label="Tasa_Conversión" 
          value={privacyMode ? '•••••' : metrics.conv} 
          trend={!privacyMode ? metrics.trend3 : undefined} 
        />
      </div>

      {/* Workspace */}
      <div className="sales-channel-workspace">
        <div className="sales-channel-workspace-header">
          <div className="sales-channel-workspace-title-group">
            <span className="sales-channel-workspace-subtitle">Configuración_Del_Canal</span>
            <h3 className="sales-channel-workspace-title">
              {activeChannel === 'librerias' && <Store size={16} className="sales-channel-workspace-title-icon" />}
              {activeChannel === 'online' && <Globe size={16} className="sales-channel-workspace-title-icon" />}
              {activeChannel === 'baas' && <Cloud size={16} className="sales-channel-workspace-title-icon" />}
              {activeChannel === 'partners' && <Building2 size={16} className="sales-channel-workspace-title-icon" />}
              CANAL_{activeChannel.toUpperCase()}
            </h3>
          </div>
          <div className="sales-channel-workspace-actions">
            <button 
              onClick={() => notify('Exportación completada', `Reporte financiero de Canal ${activeChannel.toUpperCase()} exportado correctamente.`, 'info')}
              className="sales-channel-btn-outline"
            >
              Exportar_Reporte
            </button>
            <button 
              onClick={() => selectedEntity && handleSyncEntity(selectedEntity.id, selectedEntity.name)}
              disabled={syncingId !== null || !selectedEntity}
              title={selectedEntity ? undefined : 'Abre una entidad para sincronizarla'}
              className="sales-channel-btn-primary"
              data-keep-detail
            >
              <RefreshCw size={12} className={syncingId ? 'sales-channel-spin' : ''} />
              Sincronizar_Ahora
            </button>
          </div>
        </div>

        {activeChannel === 'librerias' ? (
          <StoresPanel />
        ) : (
          <>
        <div className="module-toolbar">
          <div className="module-search-wrapper">
            <Search className="module-search-icon" size={14} />
            <input
              type="text"
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              placeholder="BUSCAR ENTIDAD O ID..."
              className="module-search-input"
            />
          </div>
        </div>

        <MasterDetailLayout isOpen={Boolean(selectedEntity)}>
          <DataTable
            data={filteredEntities}
            columns={entityColumns}
            title={<><Database size={14} className="module-panel-title-icon" /> Entidades_Integradas</>}
            subtitle={`TOTAL: ${filteredEntities.length}`}
            getRowId={(e) => e.id}
            onRowClick={(e) => setSelectedEntityId(e.id)}
            selectedRowId={selectedEntity ? selectedEntity.id : null}
            emptyText="Ninguna entidad coincide con la búsqueda"
            actionsHeader="ACCIONES"
            actionsColumn={(e) => (
              <RowActionsMenu
                actions={[
                  viewAction(() => setSelectedEntityId(e.id)),
                  { label: 'Editar márgenes', icon: SlidersHorizontal, onSelect: () => openMarginModal(e) },
                  { label: 'Forzar sincro', icon: RefreshCw, disabled: syncingId !== null, onSelect: () => handleSyncEntity(e.id, e.name) }
                ]}
              />
            )}
          />

          {selectedEntity && (
            <DetailPanel
              title="Ficha_De_Entidad"
              onClose={() => setSelectedEntityId('')}
              toolbar={
                <>
                  <button type="button" onClick={() => openMarginModal(selectedEntity)} className="module-action-btn">
                    <SlidersHorizontal size={12} /> Editar márgenes
                  </button>
                  <button
                    type="button"
                    onClick={() => handleSyncEntity(selectedEntity.id, selectedEntity.name)}
                    disabled={syncingId !== null}
                    className="module-action-btn"
                  >
                    <RefreshCw size={12} className={syncingId === selectedEntity.id ? 'sales-channel-spin' : ''} /> Forzar sincro
                  </button>
                </>
              }
            >
              <DetailIdentity
                icon={Database}
                reference={selectedEntity.id}
                name={selectedEntity.name}
                subtitle={<span className={`module-badge ${STATUS_BADGE[selectedEntity.status]}`}>{STATUS_LABELS[selectedEntity.status]}</span>}
              />

              <DetailFieldList>
                <DetailField label="Latencia conexión">{selectedEntity.apiLatency}</DetailField>
                <DetailField label="Última transmisión">{selectedEntity.lastSync}</DetailField>
                <DetailField label={selectedEntity.extraLabel}>{selectedEntity.extraValue}</DetailField>
                <DetailField label="Volumen facturado">{privacyMode ? '•••••' : selectedEntity.sales}</DetailField>
                <DetailField label="Pedidos tramitados">{privacyMode ? '•••••' : selectedEntity.orders}</DetailField>
              </DetailFieldList>
            </DetailPanel>
          )}
        </MasterDetailLayout>
          </>
        )}
      </div>

      {activeChannel !== 'librerias' && (
      <div className="sales-channel-logs-section">
        <div className="sales-channel-logs-header">
          <div className="sales-channel-logs-title-container">
            <Key size={14} className="sales-channel-logs-icon" />
            <h4 className="sales-channel-logs-title">Consola_De_Eventos_Y_Transacciones</h4>
          </div>
          <button 
            onClick={() => setClearLogsModalOpen(true)}
            className="sales-channel-logs-clear-btn"
          >
            Limpiar_Consola
          </button>
        </div>

        <div className="sales-channel-logs-table-container">
          <table className="sales-channel-logs-table">
            <thead>
              <tr>
                <th className="sales-channel-logs-th">HORA_REGISTRO</th>
                <th className="sales-channel-logs-th">IDENTIFICADOR</th>
                <th className="sales-channel-logs-th">EVENTO_SISTEMA</th>
                <th className="sales-channel-logs-th">ESTADO</th>
                <th className="sales-channel-logs-th right">REGISTRO_MÉTRICA</th>
              </tr>
            </thead>
            <tbody className="sales-channel-logs-tbody">
              {logsState.map((log, idx) => (
                <tr key={idx} className="sales-channel-logs-tr">
                  <td className="sales-channel-logs-td">{log.time}</td>
                  <td className="sales-channel-logs-td sales-channel-logs-td-id">{log.id}</td>
                  <td className="sales-channel-logs-td sales-channel-logs-td-event">{log.event}</td>
                  <td className="sales-channel-logs-td">
                    <span className={`sales-channel-logs-td-status-pill ${log.status === 'ERROR' ? 'error' : ''}`}>
                      {log.status === 'SUCCESS' ? 'ÉXITO' : log.status === 'ERROR' ? 'ERROR' : log.status}
                    </span>
                  </td>
                  <td className="sales-channel-logs-td sales-channel-logs-td-msg">{log.log}</td>
                </tr>
              ))}
              {logsState.length === 0 && (
                <tr>
                  <td colSpan={5} className="sales-channel-logs-empty">
                    Consola vacía. Ejecute alguna acción corporativa para registrar eventos contables o de sincronización.
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>
      </div>
      )}

      {marginModalOpen && (
        <div className="sales-channel-modal-overlay" data-keep-detail>
          <div className="sales-channel-modal-content sm">
            <div className="sales-channel-modal-header">
              <div className="sales-channel-modal-header-left">
                <div className="sales-channel-modal-icon-container" style={{ padding: '0.125rem' }}>
                   <div style={{ width: '0.5rem', height: '0.5rem', backgroundColor: 'var(--color-brand-green)' }} />
                </div>
                <h3 className="sales-channel-modal-title">Ajustar_Margen_Distribución</h3>
              <button onClick={() => setMarginModalOpen(false)} className="sales-channel-modal-close"><X size={16} /></button>
              </div>
            </div>
            <div className="sales-channel-modal-body">
              <div className="sales-channel-margin-info">
                <p className="sales-channel-margin-info-label">Entidad_Seleccionada</p>
                <p className="sales-channel-margin-info-value">{marginEntity?.name}</p>
              </div>
              <div className="sales-channel-form-group">
                <label className="sales-channel-form-label">Margen Comercial / Distribución (%)</label>
                <input 
                  type="text"
                  value={customMargin}
                  onChange={(e) => setCustomMargin(e.target.value)}
                  className="sales-channel-form-input"
                  placeholder="EJ. 45%"
                />
                <p className="sales-channel-form-desc">El margen de distribución se aplicará de forma global a todos los títulos asignados a este canal.</p>
              </div>
            </div>
            <div className="sales-channel-modal-footer">
              <button type="button" onClick={() => setMarginModalOpen(false)} className="sales-channel-btn-secondary">Cancelar</button>
              <button type="button" onClick={handleApplyMargin} className="sales-channel-btn-submit">Aplicar_Margen</button>
            </div>
          </div>
        </div>
      )}

      {activeActionModalId && (
        <div className="sales-channel-modal-overlay" data-keep-detail>
          <div className={`sales-channel-modal-content ${activeActionModalId === 'canal_pedido' ? 'lg' : 'md'}`}>
            <div className="sales-channel-modal-header">
              <div className="sales-channel-modal-header-left">
                <div className="sales-channel-modal-icon-container">
                  {activeActionModalId === 'canal_pedido' && <ShoppingBag size={16} />}
                  {activeActionModalId === 'canal_devolucion' && <Undo2 size={16} />}
                  {activeActionModalId === 'canal_nueva_api' && <Plus size={16} />}
                  {activeActionModalId === 'canal_margenes' && <SlidersHorizontal size={16} />}
                  {activeActionModalId === 'canal_logs' && <Key size={16} />}
                </div>
                <div>
                  <h3 className="sales-channel-modal-title">
                    {activeActionModalId === 'canal_pedido' && 'Nuevo Pedido Entrante Canal'}
                    {activeActionModalId === 'canal_devolucion' && 'Registrar Devolución y Reembolso'}
                    {activeActionModalId === 'canal_nueva_api' && 'Dar de Alta Integración API'}
                    {activeActionModalId === 'canal_margenes' && 'Ajustar Margen de Acuerdo Comercial'}
                    {activeActionModalId === 'canal_logs' && 'Historial de Logs Contables y Sincro'}
                  </h3>
                  <p className="sales-channel-modal-desc">
                    {activeActionModalId === 'canal_pedido' && 'Registra un pedido comercial de canal y crea el asiento de diario contable'}
                    {activeActionModalId === 'canal_devolucion' && 'Procesa la devolución de stock y el abono correspondiente según la norma PGC'}
                    {activeActionModalId === 'canal_nueva_api' && 'Conecta un nuevo terminal de librería, plataforma ecommerce o partner'}
                    {activeActionModalId === 'canal_margenes' && 'Configura los márgenes de descuento comercial aplicados al distribuidor'}
                    {activeActionModalId === 'canal_logs' && 'Visor en tiempo real de transacciones y llamadas API'}
                  </p>
                </div>
              </div>
              <button onClick={() => setActiveActionModalId(null)} className="sales-channel-modal-close"><X size={16} /></button>
            </div>

            <div className="sales-channel-modal-body">
              {activeActionModalId === 'canal_nueva_api' && (
                <form onSubmit={handleSubmitNewIntegration} style={{ display: 'flex', flexDirection: 'column', gap: '1rem' }}>
                  <div className="sales-channel-form-group">
                    <label className="sales-channel-form-label">Nombre del Socio / Plataforma</label>
                    <input 
                      type="text" 
                      required
                      placeholder="Ej. Casa del Libro Sevilla"
                      value={newEntityName}
                      onChange={(e) => setNewEntityName(e.target.value)}
                      className="sales-channel-form-input"
                    />
                  </div>
                  <div className="sales-channel-form-grid">
                    <div className="sales-channel-form-group">
                      <label className="sales-channel-form-label">ID Referencia Único</label>
                      <input 
                        type="text" 
                        required
                        placeholder="Ej. CDL-SEV-01"
                        value={newEntityId}
                        onChange={(e) => setNewEntityId(e.target.value)}
                        className="sales-channel-form-input"
                        style={{ textTransform: 'uppercase' }}
                      />
                    </div>
                    <div className="sales-channel-form-group">
                      <label className="sales-channel-form-label">Protocolo Conexión</label>
                      <select 
                        value={newEntityStatus}
                        onChange={(e) => setNewEntityStatus(e.target.value as any)}
                        className="sales-channel-form-input"
                      >
                        <option value="online">CONECTADO (API REST)</option>
                        <option value="offline">DESCONECTADO (SINCRO EN LOTES)</option>
                        <option value="error">ERROR (REINTENTOS)</option>
                      </select>
                    </div>
                  </div>

                  <div className="sales-channel-form-grid">
                    <div className="sales-channel-form-group">
                      <label className="sales-channel-form-label">Latencia Base (ms)</label>
                      <input 
                        type="text" 
                        value={newEntityLatency}
                        onChange={(e) => setNewEntityLatency(e.target.value)}
                        className="sales-channel-form-input"
                      />
                    </div>
                    <div className="sales-channel-form-group">
                      <label className="sales-channel-form-label">Etiqueta Operativa</label>
                      <input 
                        type="text" 
                        value={newEntityExtraLabel}
                        onChange={(e) => setNewEntityExtraLabel(e.target.value)}
                        className="sales-channel-form-input"
                      />
                    </div>
                  </div>

                  <div className="sales-channel-form-group">
                    <label className="sales-channel-form-label">Valor Operativo Inicial</label>
                    <input 
                      type="text" 
                      value={newEntityExtraValue}
                      onChange={(e) => setNewEntityExtraValue(e.target.value)}
                      className="sales-channel-form-input"
                    />
                  </div>

                  <div className="sales-channel-modal-footer" style={{ padding: '1rem 0 0', marginTop: '0.5rem' }}>
                    <button type="button" onClick={() => setActiveActionModalId(null)} className="sales-channel-btn-secondary">Cancelar</button>
                    <button type="submit" className="sales-channel-btn-submit">Establecer Integración API</button>
                  </div>
                </form>
              )}

              {activeActionModalId === 'canal_margenes' && (
                <form onSubmit={handleSubmitMarginChange} style={{ display: 'flex', flexDirection: 'column', gap: '1rem' }}>
                   <div className="sales-channel-form-group">
                    <label className="sales-channel-form-label">Socio Comercial</label>
                    <select 
                      value={marginEntityId}
                      onChange={(e) => setMarginEntityId(e.target.value)}
                      className="sales-channel-form-input"
                    >
                      {currentEntities.map(ent => (
                        <option key={ent.id} value={ent.id}>{ent.name}</option>
                      ))}
                    </select>
                  </div>
                  <div className="sales-channel-form-group">
                    <label className="sales-channel-form-label">Margen Distribuidor / Partner (%)</label>
                    <input 
                      type="text" 
                      required
                      value={marginPercent}
                      onChange={(e) => setMarginPercent(e.target.value)}
                      className="sales-channel-form-input"
                    />
                  </div>
                  <div className="sales-channel-modal-footer" style={{ padding: '1rem 0 0', marginTop: '0.5rem' }}>
                    <button type="button" onClick={() => setActiveActionModalId(null)} className="sales-channel-btn-secondary">Cancelar</button>
                    <button type="submit" className="sales-channel-btn-submit">Guardar Nuevo Margen</button>
                  </div>
                </form>
              )}
              
              {activeActionModalId === 'canal_pedido' && (
                <form onSubmit={handleSubmitChannelOrder} style={{ display: 'flex', flexDirection: 'column', gap: '1rem' }}>
                  <div className="sales-channel-form-grid">
                    <div className="sales-channel-form-group">
                      <label className="sales-channel-form-label">Canal de Distribución</label>
                      <input 
                        type="text" 
                        readOnly 
                        value={`CANAL_${activeChannel.toUpperCase()}`}
                        className="sales-channel-form-input readonly"
                      />
                    </div>
                    <div className="sales-channel-form-group">
                      <label className="sales-channel-form-label">Entidad de Enlace</label>
                      <select 
                        value={orderEntityId}
                        onChange={(e) => setOrderEntityId(e.target.value)}
                        className="sales-channel-form-input"
                      >
                        {currentEntities.map(ent => (
                          <option key={ent.id} value={ent.id}>{ent.name} ({ent.id})</option>
                        ))}
                      </select>
                    </div>
                  </div>

                  <div className="sales-channel-form-group">
                    <label className="sales-channel-form-label">Identificar Cliente Destinatario <span style={{ color: 'var(--color-brand-green)' }}>*</span></label>
                    <div className="sales-channel-search-client-container">
                      <Search size={14} className="sales-channel-search-icon" style={{ zIndex: 10 }} />
                      <input
                        type="text"
                        placeholder="Escriba para buscar cliente por nombre o NIF..."
                        value={clientSearchQuery}
                        onChange={(e) => {
                          setClientSearchQuery(e.target.value);
                          setShowClientDropdown(true);
                        }}
                        onFocus={() => setShowClientDropdown(true)}
                        className="sales-channel-form-input"
                        style={{ paddingLeft: '2rem', paddingRight: '2rem' }}
                      />
                      {clientSearchQuery && (
                        <button
                          type="button"
                          onClick={() => {
                            setClientSearchQuery('');
                            setSelectedClient(null);
                          }}
                          className="sales-channel-modal-close"
                          style={{ position: 'absolute', right: '0.5rem', top: '50%', transform: 'translateY(-50%)' }}
                        >
                          <X size={12} />
                        </button>
                      )}

                      {showClientDropdown && (
                        <>
                          <div style={{ position: 'fixed', inset: 0, zIndex: 40 }} onClick={() => setShowClientDropdown(false)} />
                          <div className="sales-channel-client-dropdown">
                            {PRESET_CLIENTS.filter(c => 
                              c.name.toLowerCase().includes(clientSearchQuery.toLowerCase()) ||
                              c.cif.toLowerCase().includes(clientSearchQuery.toLowerCase()) ||
                              c.contactPerson.toLowerCase().includes(clientSearchQuery.toLowerCase())
                            ).map(client => (
                              <button
                                key={client.id}
                                type="button"
                                onClick={() => {
                                  setSelectedClient(client);
                                  setClientSearchQuery(client.name);
                                  setShowClientDropdown(false);
                                }}
                                className="sales-channel-client-option"
                              >
                                <div className="sales-channel-client-option-header">
                                  <span>{client.name}</span>
                                  <span className="sales-channel-client-option-cif">{client.cif}</span>
                                </div>
                                <div className="sales-channel-client-option-desc">
                                  Contacto: {client.contactPerson} ({client.email})
                                </div>
                              </button>
                            ))}
                            {PRESET_CLIENTS.filter(c => 
                              c.name.toLowerCase().includes(clientSearchQuery.toLowerCase()) ||
                              c.cif.toLowerCase().includes(clientSearchQuery.toLowerCase()) ||
                              c.contactPerson.toLowerCase().includes(clientSearchQuery.toLowerCase())
                            ).length === 0 && (
                              <div style={{ padding: '0.75rem', fontSize: '0.625rem', color: 'var(--color-brand-text)', fontStyle: 'italic', textTransform: 'uppercase' }}>
                                Ningún cliente coincide con la búsqueda
                              </div>
                            )}
                          </div>
                        </>
                      )}
                    </div>

                    {selectedClient && (
                      <div className="sales-channel-client-summary">
                        <div className="sales-channel-client-summary-left">
                          <p style={{ color: '#fff', fontWeight: 'bold' }}>{selectedClient.name}</p>
                          <p>NIF/CIF: <span style={{ color: '#fff' }}>{selectedClient.cif}</span> | Tel: <span style={{ color: '#fff' }}>{selectedClient.phone}</span></p>
                          <p>Dirección: <span style={{ color: '#fff' }}>{selectedClient.address}</span></p>
                        </div>
                        <div className="sales-channel-client-summary-right">
                          <p style={{ color: 'var(--color-brand-green)', fontWeight: 'bold' }}>Persona de Contacto</p>
                          <p style={{ color: '#fff' }}>{selectedClient.contactPerson}</p>
                          <p style={{ color: '#fff', textTransform: 'lowercase', fontFamily: 'sans-serif', fontSize: '0.5625rem' }}>{selectedClient.email}</p>
                        </div>
                      </div>
                    )}
                  </div>

                  <div className="sales-channel-invoice-lines">
                    <div className="sales-channel-invoice-lines-header">
                      <label className="sales-channel-invoice-lines-title">
                        Líneas de Pedido (Títulos / Catálogo)
                      </label>
                      <button
                        type="button"
                        onClick={addInvoiceLine}
                        className="sales-channel-btn-add-line"
                      >
                        + Añadir Línea
                      </button>
                    </div>

                    <div className="sales-channel-lines-container">
                      {invoiceLines.map((line) => (
                        <div key={line.id} className="sales-channel-line-item">
                          <div className="sales-channel-line-field">
                            <span className="sales-channel-line-label">Libro / Catálogo</span>
                            <select
                              value={line.product?.id || ''}
                              onChange={(e) => handleProductChange(line.id, e.target.value)}
                              className="sales-channel-line-input"
                            >
                              <option value="">[ Selecciona libro ]</option>
                              {MOCK_BOOKS.map(p => (
                                <option key={p.id} value={p.id}>{p.title} ({p.price}€)</option>
                              ))}
                            </select>
                          </div>
                          <div className="sales-channel-line-field">
                            <span className="sales-channel-line-label">Concepto</span>
                            <input
                              type="text"
                              value={line.customName}
                              onChange={(e) => handleLineFieldChange(line.id, 'customName', e.target.value)}
                              placeholder="Concepto..."
                              className="sales-channel-line-input"
                            />
                          </div>
                          <div className="sales-channel-line-field">
                            <span className="sales-channel-line-label">Cant.</span>
                            <input
                              type="number"
                              min="1"
                              value={line.quantity}
                              onChange={(e) => handleLineFieldChange(line.id, 'quantity', parseInt(e.target.value) || 1)}
                              className="sales-channel-line-input"
                              style={{ textAlign: 'center' }}
                            />
                          </div>
                          <div className="sales-channel-line-field">
                            <span className="sales-channel-line-label">Precio</span>
                            <input
                              type="number"
                              min="0"
                              step="0.01"
                              value={line.price}
                              onChange={(e) => handleLineFieldChange(line.id, 'price', parseFloat(e.target.value) || 0)}
                              className="sales-channel-line-input"
                            />
                          </div>
                          <div className="sales-channel-line-field">
                            <span className="sales-channel-line-label">IVA</span>
                            <select
                              value={line.vat}
                              onChange={(e) => handleLineFieldChange(line.id, 'vat', parseInt(e.target.value) || 4)}
                              className="sales-channel-line-input"
                            >
                              <option value={4}>4%</option>
                              <option value={10}>10%</option>
                              <option value={21}>21%</option>
                            </select>
                          </div>
                          <div className="sales-channel-line-field" style={{ alignItems: 'center', justifyContent: 'flex-end' }}>
                            <button
                              type="button"
                              onClick={() => removeInvoiceLine(line.id)}
                              disabled={invoiceLines.length <= 1}
                              className="sales-channel-btn-delete-line"
                            >
                              <Trash2 size={12} />
                            </button>
                          </div>
                        </div>
                      ))}
                    </div>
                  </div>

                  <div className="sales-channel-summary-box">
                    <div className="sales-channel-summary-breakdown">
                      <p>Desglose de Base Imponible:</p>
                      <div className="sales-channel-summary-breakdown-vals">
                        <span>Base: {invoiceLines.reduce((a,b) => a + (b.quantity * b.price), 0).toFixed(2)}€</span>
                        <span>IVA: {invoiceLines.reduce((a,b) => a + ((b.quantity * b.price) * (b.vat/100)), 0).toFixed(2)}€</span>
                      </div>
                    </div>
                    <div className="sales-channel-summary-total">
                      <p>Total Asiento / Pedido:</p>
                      <p>{(invoiceLines.reduce((a,b) => a + (b.quantity * b.price), 0) + invoiceLines.reduce((a,b) => a + ((b.quantity * b.price) * (b.vat/100)), 0)).toFixed(2)}€</p>
                    </div>
                  </div>

                  <div className="sales-channel-modal-footer" style={{ padding: '1rem 0 0', marginTop: '0.5rem' }}>
                    <button type="button" onClick={() => setActiveActionModalId(null)} className="sales-channel-btn-secondary">Cancelar</button>
                    <button type="submit" className="sales-channel-btn-submit">Registrar Pedido de Canal</button>
                  </div>
                </form>
              )}

              {activeActionModalId === 'canal_devolucion' && (
                <form onSubmit={handleSubmitReturn} style={{ display: 'flex', flexDirection: 'column', gap: '1rem' }}>
                  <div className="sales-channel-form-group">
                    <label className="sales-channel-form-label">Entidad de Retorno</label>
                    <select 
                      value={returnEntityId}
                      onChange={(e) => setReturnEntityId(e.target.value)}
                      className="sales-channel-form-input"
                    >
                      {currentEntities.map(ent => (
                        <option key={ent.id} value={ent.id}>{ent.name} ({ent.id})</option>
                      ))}
                    </select>
                  </div>

                  <div className="sales-channel-form-group">
                    <label className="sales-channel-form-label">Identificar Cliente</label>
                    <div className="sales-channel-search-client-container">
                      <Search size={14} className="sales-channel-search-icon" style={{ zIndex: 10 }} />
                      <input
                        type="text"
                        placeholder="Escriba para filtrar cliente..."
                        value={returnClientSearchQuery}
                        onChange={(e) => {
                          setReturnClientSearchQuery(e.target.value);
                          setShowReturnClientDropdown(true);
                        }}
                        onFocus={() => setShowReturnClientDropdown(true)}
                        className="sales-channel-form-input"
                        style={{ paddingLeft: '2rem', paddingRight: '2rem' }}
                      />
                      {returnClientSearchQuery && (
                        <button
                          type="button"
                          onClick={() => {
                            setReturnClientSearchQuery('');
                            setSelectedReturnClient(null);
                          }}
                          className="sales-channel-modal-close"
                          style={{ position: 'absolute', right: '0.5rem', top: '50%', transform: 'translateY(-50%)' }}
                        >
                          <X size={12} />
                        </button>
                      )}

                      {showReturnClientDropdown && (
                        <>
                          <div style={{ position: 'fixed', inset: 0, zIndex: 40 }} onClick={() => setShowReturnClientDropdown(false)} />
                          <div className="sales-channel-client-dropdown" style={{ maxHeight: '10rem' }}>
                            {PRESET_CLIENTS.filter(c => c.name.toLowerCase().includes(returnClientSearchQuery.toLowerCase())).map(client => (
                              <button
                                key={client.id}
                                type="button"
                                onClick={() => {
                                  setSelectedReturnClient(client);
                                  setReturnClientSearchQuery(client.name);
                                  setShowReturnClientDropdown(false);
                                }}
                                className="sales-channel-client-option"
                              >
                                {client.name} ({client.cif})
                              </button>
                            ))}
                          </div>
                        </>
                      )}
                    </div>

                    {selectedReturnClient && (
                      <div className="sales-channel-client-summary warning" style={{ flexDirection: 'column', gap: '0.25rem' }}>
                        <p style={{ color: '#fff', fontWeight: 'bold' }}>{selectedReturnClient.name}</p>
                        <p>NIF: {selectedReturnClient.cif} | Contacto: {selectedReturnClient.contactPerson}</p>
                      </div>
                    )}
                  </div>

                  <div className="sales-channel-form-grid">
                    <div className="sales-channel-form-group">
                      <label className="sales-channel-form-label">Libro Devuelto</label>
                      <select 
                        value={returnProduct?.id || ''}
                        onChange={(e) => {
                          const p = MOCK_BOOKS.find(x => x.id === e.target.value);
                          if (p) {
                            setReturnProduct(p);
                            setReturnPrice(p.price);
                            setReturnVat(4);
                          }
                        }}
                        className="sales-channel-form-input"
                      >
                        {MOCK_BOOKS.map(p => (
                          <option key={p.id} value={p.id}>{p.title}</option>
                        ))}
                      </select>
                    </div>

                    <div className="sales-channel-form-group">
                      <label className="sales-channel-form-label">Cantidad Devuelta</label>
                      <input 
                        type="number"
                        min="1"
                        value={returnQty}
                        onChange={(e) => setReturnQty(parseInt(e.target.value) || 1)}
                        className="sales-channel-form-input"
                      />
                    </div>
                  </div>

                  <div className="sales-channel-form-grid">
                    <div className="sales-channel-form-group">
                      <label className="sales-channel-form-label">Precio Reembolso Unit.</label>
                      <input 
                        type="number"
                        min="0"
                        step="0.01"
                        value={returnPrice}
                        onChange={(e) => setReturnPrice(parseFloat(e.target.value) || 0)}
                        className="sales-channel-form-input"
                      />
                    </div>

                    <div className="sales-channel-form-group">
                      <label className="sales-channel-form-label">IVA aplicado</label>
                      <select 
                        value={returnVat}
                        onChange={(e) => setReturnVat(parseInt(e.target.value) || 4)}
                        className="sales-channel-form-input"
                      >
                        <option value={4}>IVA Reducido (4%)</option>
                        <option value={10}>IVA Editorial (10%)</option>
                        <option value={21}>IVA General (21%)</option>
                      </select>
                    </div>
                  </div>

                  <div className="sales-channel-return-summary">
                    <span className="sales-channel-return-summary-label">Abono Total Estimado:</span>
                    <p className="sales-channel-return-summary-value">{(returnQty * returnPrice * (1 + returnVat / 100)).toFixed(2)}€</p>
                  </div>

                  <div className="sales-channel-modal-footer" style={{ padding: '1rem 0 0', marginTop: '0.5rem' }}>
                    <button type="button" onClick={() => setActiveActionModalId(null)} className="sales-channel-btn-secondary">Cancelar</button>
                    <button type="submit" className="sales-channel-btn-submit sales-channel-btn-danger">Contabilizar Nota de Abono</button>
                  </div>
                </form>
              )}

              {activeActionModalId === 'canal_logs' && (
                <div style={{ display: 'flex', flexDirection: 'column', gap: '1rem' }}>
                  <div className="sales-channel-logs-modal-header">
                    <span className="sales-channel-logs-modal-title">Canal de telemetría activo</span>
                    <button 
                      onClick={() => {
                        const timestamp = new Date().toISOString().replace('T', ' ').substring(0, 19);
                        const idRandom = `${activeChannel.toUpperCase()}-${Math.floor(100 + Math.random()*900)}`;
                        const newLog = {
                          time: timestamp,
                          id: idRandom,
                          event: 'Llamada API simulada (Test Ping)',
                          status: 'SUCCESS',
                          log: 'Conexión verificada: Payload JSON validado correctamente.'
                        };
                        setLogsState(prev => [newLog, ...prev]);
                        notify('Simulación API', 'Simulación de llamada API disparada con éxito.', 'info');
                      }}
                      className="sales-channel-logs-modal-btn"
                    >
                      Simular llamada API
                    </button>
                  </div>

                  <div className="sales-channel-logs-modal-box">
                    {logsState.map((log, idx) => (
                      <div key={idx} className="sales-channel-log-item">
                        <div className="sales-channel-log-item-header">
                          <span>{log.time} | ID: {log.id}</span>
                          <span className="sales-channel-log-item-status">{log.status}</span>
                        </div>
                        <p className="sales-channel-log-item-event">{log.event}</p>
                        <p className="sales-channel-log-item-msg">{log.log}</p>
                      </div>
                    ))}
                    {logsState.length === 0 && (
                      <p style={{ textAlign: 'center', padding: '1.5rem', color: 'var(--color-brand-text)', fontStyle: 'italic' }}>No hay logs en el historial.</p>
                    )}
                  </div>

                  <div className="sales-channel-modal-footer" style={{ padding: '1rem 0 0', marginTop: '0.5rem' }}>
                    <button type="button" onClick={() => setActiveActionModalId(null)} className="sales-channel-btn-secondary" style={{ flex: 1, backgroundColor: 'var(--color-brand-blue)', color: '#fff' }}>Cerrar Visor</button>
                  </div>
                </div>
              )}
            </div>
          </div>
        </div>
      )}

      <Modal
        isOpen={clearLogsModalOpen}
        type="warning"
        title="Limpiar consola"
        message="¿Seguro que desea limpiar la consola de logs?"
        confirmText="Limpiar"
        cancelText="Cancelar"
        onConfirm={() => {
          setLogsState([]);
          setClearLogsModalOpen(false);
          notify('Éxito', 'Consola de logs limpiada', 'info');
        }}
        onCancel={() => setClearLogsModalOpen(false)}
      />
    </div>
  );
};
